/**
 * WorkspaceJourneyPage
 *
 * System-wide graphical journey: all items on a shared time axis.
 * Filter by search, status, or category; press Play to sweep the
 * playhead forward through time, revealing events in order.
 *
 * Each item occupies one row. Event dots are coloured by type
 * (same palette as ItemJourneyPage). Click a dot for details;
 * click the item name for the full per-item graphical journey.
 *
 * Route: /prioritization/workspace-journey
 */

import { useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Play, Pause, RotateCcw, Search, X, ExternalLink, Activity,
  GitBranch, ChevronDown, ChevronUp,
} from 'lucide-react'
import { useItemsStore } from '../../stores/prioritization/itemsStore'
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore'
import { useSessionStore } from '../../stores/prioritization/sessionStore'
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore'
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap'
import {
  EVENT_CONFIG,
  LANES,
  RULER_HEIGHT,
  deriveJourneyEvents,
  usePlayhead,
  buildRulerTicks,
  formatDateShort,
  DetailPanel,
  type JourneyEvent,
  type Lane,
} from './ItemJourneyPage'

// ─── Layout constants ─────────────────────────────────────────────────────────

const LABEL_W    = 220   // fixed-width left column (item names)
const ROW_H      = 52    // height of each item row
const DOT_R      = 8     // event dot radius
const SIDE_PAD   = 60    // left/right padding inside the canvas
const MIN_GAP_PX = 72    // minimum horizontal gap between dots on same row

const ITEM_CAP = 200     // max items rendered (performance guard)

// ─── Status options ───────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { id: 'all',      label: 'All' },
  { id: 'active',   label: 'Active' },
  { id: 'deferred', label: 'Deferred' },
  { id: 'archived', label: 'Archived' },
]

// ─── Canvas width helper ──────────────────────────────────────────────────────

function computeCanvasWidth(allEvents: JourneyEvent[][]): number {
  const maxPerRow = Math.max(...allEvents.map((evs) => evs.length), 1)
  return Math.max(SIDE_PAD * 2 + maxPerRow * MIN_GAP_PX, 900)
}

// ─── Per-row event x-positions (spaced to respect MIN_GAP_PX) ────────────────

function spacedXForRow(events: JourneyEvent[], toX: (t: number) => number): Map<string, number> {
  const map = new Map<string, number>()
  if (events.length === 0) return map
  const raw = events.map((e) => toX(e.timestamp.getTime()))
  const spaced = [...raw]
  for (let i = 1; i < spaced.length; i++) {
    if (spaced[i] - spaced[i - 1] < MIN_GAP_PX) spaced[i] = spaced[i - 1] + MIN_GAP_PX
  }
  events.forEach((e, i) => map.set(e.id, spaced[i]))
  return map
}

// ─── Time ruler strip ─────────────────────────────────────────────────────────

function TimeRuler({
  tMin, tMax, canvasWidth,
}: {
  tMin: number; tMax: number; canvasWidth: number
}) {
  const toX = useCallback(
    (t: number) => SIDE_PAD + ((t - tMin) / (tMax - tMin || 1)) * (canvasWidth - SIDE_PAD * 2),
    [tMin, tMax, canvasWidth],
  )
  const ticks = useMemo(() => buildRulerTicks(tMin, tMax, toX), [tMin, tMax, toX])

  return (
    <svg width={canvasWidth} height={RULER_HEIGHT} className="select-none block">
      <rect x={0} y={0} width={canvasWidth} height={RULER_HEIGHT} fill="white" fillOpacity={0.9} />
      <line x1={SIDE_PAD} y1={RULER_HEIGHT - 1} x2={canvasWidth - SIDE_PAD} y2={RULER_HEIGHT - 1}
        stroke="#e5e7eb" strokeWidth={1} />
      {ticks.map((tick, i) => (
        <g key={i}>
          <line x1={tick.x} y1={RULER_HEIGHT - 6} x2={tick.x} y2={RULER_HEIGHT}
            stroke="#d1d5db" strokeWidth={1} />
          <text x={tick.x} y={RULER_HEIGHT - 10} textAnchor="middle" fontSize={10}
            fill="#9ca3af" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="500">
            {tick.label}
          </text>
        </g>
      ))}
    </svg>
  )
}

// ─── Single item row SVG ──────────────────────────────────────────────────────

function ItemRowSvg({
  events,
  canvasWidth,
  playheadProgress,
  tMin,
  tMax,
  selectedId,
  onSelect,
  showPlayhead,
}: {
  events: JourneyEvent[]
  canvasWidth: number
  playheadProgress: number
  tMin: number
  tMax: number
  selectedId: string | null
  onSelect: (id: string | null) => void
  showPlayhead: boolean
}) {
  const toX = useCallback(
    (t: number) => SIDE_PAD + ((t - tMin) / (tMax - tMin || 1)) * (canvasWidth - SIDE_PAD * 2),
    [tMin, tMax, canvasWidth],
  )
  const xMap = useMemo(() => spacedXForRow(events, toX), [events, toX])
  const playheadX = SIDE_PAD + playheadProgress * (canvasWidth - SIDE_PAD * 2)
  const midY = ROW_H / 2

  return (
    <svg width={canvasWidth} height={ROW_H} className="select-none block">
      {/* Thin baseline rail */}
      {events.length > 0 && (() => {
        const xs = events.map((e) => xMap.get(e.id) ?? SIDE_PAD)
        const x1 = Math.min(...xs) - 10
        const x2 = Math.max(...xs) + 10
        return <line x1={x1} y1={midY} x2={x2} y2={midY} stroke="#e5e7eb" strokeWidth={1.5} strokeLinecap="round" />
      })()}

      {/* Event dots */}
      {events.map((event) => {
        const x = xMap.get(event.id) ?? SIDE_PAD
        const cfg = EVENT_CONFIG[event.type]
        const evProg = (event.timestamp.getTime() - tMin) / (tMax - tMin || 1)
        const isRevealed = playheadProgress === 0 || playheadProgress >= evProg
        const isSelected = event.id === selectedId

        return (
          <g
            key={event.id}
            opacity={isRevealed ? 1 : 0.12}
            style={{ transition: 'opacity 0.3s', cursor: 'pointer' }}
            onClick={() => onSelect(isSelected ? null : event.id)}
          >
            {/* Selection halo */}
            {isSelected && (
              <circle cx={x} cy={midY} r={DOT_R + 6} fill={cfg.glowHex} opacity={0.3} />
            )}
            {/* Main dot */}
            <circle
              cx={x} cy={midY}
              r={isSelected ? DOT_R + 2 : DOT_R}
              fill={cfg.hex}
              stroke="white"
              strokeWidth={2}
            />
            {/* Icon */}
            <foreignObject
              x={x - DOT_R + 1} y={midY - DOT_R + 1}
              width={(DOT_R - 1) * 2} height={(DOT_R - 1) * 2}
              style={{ pointerEvents: 'none' }}
            >
              <div
                // @ts-expect-error xmlns required
                xmlns="http://www.w3.org/1999/xhtml"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
              >
                <cfg.icon style={{ width: 9, height: 9, color: 'white' }} />
              </div>
            </foreignObject>
            {/* Date label below dot */}
            <text x={x} y={midY + DOT_R + 12} textAnchor="middle" fontSize={8}
              fill={isSelected ? '#374151' : '#9ca3af'}
              fontFamily="system-ui, -apple-system, sans-serif">
              {formatDateShort(event.timestamp)}
            </text>
          </g>
        )
      })}

      {/* Playhead */}
      {showPlayhead && playheadProgress > 0 && playheadProgress < 1 && (
        <line
          x1={playheadX} y1={0} x2={playheadX} y2={ROW_H}
          stroke="#6366f1" strokeWidth={1.5} strokeOpacity={0.75}
        />
      )}
    </svg>
  )
}

// ─── Lane legend ──────────────────────────────────────────────────────────────

function LaneLegend() {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
      >
        Legend
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {expanded && (
        <div className="flex flex-wrap gap-2 ml-2">
          {LANES.map((lane: Lane) => (
            <span
              key={lane.id}
              className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${lane.bgClass} ${lane.textClass}`}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: lane.railHex }} />
              {lane.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function WorkspaceJourneyPage() {
  const allItems     = useItemsStore((s: any) => s.items)
  const sessions     = useSessionStore((s) => s.sessions)
  const cycles       = useDegapProcessStore((s) => s.cycles)
  const allGroups    = useItemRelationshipsStore((s) => s.groups)
  const allMemberships = useItemRelationshipsStore((s) => s.groupMemberships)
  const allEdges     = useItemRelationshipsStore((s) => s.edges)

  const [search,       setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedId,   setSelectedId]   = useState<string | null>(null)

  const { progress: playheadProgress, isPlaying, play, pause, reset } = usePlayhead()

  // ── Filter items ──────────────────────────────────────────────────────────

  const filteredItems = useMemo(() => {
    let items: typeof allItems = allItems
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter((i: any) => i.title?.toLowerCase().includes(q))
    }
    if (statusFilter !== 'all') {
      items = items.filter((i: any) =>
        i.exit_status === statusFilter || i.status === statusFilter,
      )
    }
    return items.slice(0, ITEM_CAP)
  }, [allItems, search, statusFilter])

  // ── Derive journey events for each item ───────────────────────────────────

  const itemData = useMemo<{ item: any; events: JourneyEvent[] }[]>(() => {
    return filteredItems.map((item: any) => {
      const groups = allGroups.filter((g) =>
        allMemberships.some((m) => m.groupId === g.id && m.itemId === item.id),
      )
      const edges = allEdges.filter(
        (e) => e.sourceItemId === item.id || e.targetItemId === item.id,
      )
      const events = deriveJourneyEvents(item, sessions, cycles, groups, edges, allItems)
      return { item, events }
    })
  }, [filteredItems, allGroups, allMemberships, allEdges, sessions, cycles, allItems])

  // ── Global time range ─────────────────────────────────────────────────────

  const { tMin, tMax } = useMemo(() => {
    let min = Infinity
    let max = -Infinity
    for (const { events } of itemData) {
      for (const e of events) {
        const t = e.timestamp.getTime()
        if (t < min) min = t
        if (t > max) max = t
      }
    }
    const now = Date.now()
    return {
      tMin: isFinite(min) ? min : now - 86400000 * 30,
      tMax: isFinite(max) ? max : now,
    }
  }, [itemData])

  // ── Canvas width ──────────────────────────────────────────────────────────

  const canvasWidth = useMemo(
    () => computeCanvasWidth(itemData.map(({ events }) => events)),
    [itemData],
  )

  // ── Selected event resolution ─────────────────────────────────────────────

  const selectedEvent = useMemo<JourneyEvent | null>(() => {
    if (!selectedId) return null
    for (const { events } of itemData) {
      const found = events.find((e) => e.id === selectedId)
      if (found) return found
    }
    return null
  }, [itemData, selectedId])

  // ── Actor display map for detail panel ────────────────────────────────────

  const allActorIds = useMemo(() => {
    const ids: (string | null)[] = []
    for (const { events } of itemData) ids.push(...events.map((e) => e.actor ?? null))
    return ids
  }, [itemData])
  const actorDisplayMap = useActorDisplayMap(allActorIds)

  // ── Stats ──────────────────────────────────────────────────────────────────

  const totalEvents = useMemo(
    () => itemData.reduce((sum, { events }) => sum + events.length, 0),
    [itemData],
  )

  const isCapped = filteredItems.length === ITEM_CAP && allItems.length > ITEM_CAP

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-screen bg-[var(--bg-raised)] overflow-hidden">

      {/* ── Top bar ── */}
      <header className="flex-shrink-0 bg-[var(--bg-raised)] border-b border-[var(--rule)] px-4 py-3 z-20">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Title */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-7 h-7 bg-[var(--slate-dim)] flex items-center justify-center">
              <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-[var(--text-pri)] leading-tight">Workspace Journey</h1>
              <p className="text-xs text-[var(--text-ter)] leading-tight">
                {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
                {isCapped && <span className="ml-1 text-[var(--mustard)]">(capped at {ITEM_CAP})</span>}
                {' · '}{totalEvents} event{totalEvents !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[160px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name…"
              className="w-full pl-8 pr-7 py-1.5 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)]"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Status filter chips */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setStatusFilter(opt.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  statusFilter === opt.id
                    ? 'bg-[var(--slate)] text-white border-[var(--slate)]'
                    : 'bg-[var(--bg-raised)] text-[var(--text-ter)] border-[var(--rule)] hover:border-[var(--rule)] hover:text-[var(--text-sec)]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Legend */}
          <LaneLegend />

          {/* Playhead controls */}
          <div className="flex items-center gap-1.5 bg-[var(--surface)] border border-[var(--rule)] px-2 py-1 flex-shrink-0">
            <button
              type="button"
              onClick={isPlaying ? pause : play}
              className="p-1 hover:bg-[var(--hover-tint)] text-[var(--text-sec)] hover:text-[var(--text-ter)] transition-colors"
              title={isPlaying ? 'Pause' : 'Play journey forward in time'}
            >
              {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={reset}
              className="p-1 hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
              title="Reset"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
            {/* Progress bar */}
            <div className="w-24 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden mx-1">
              <div
                className="h-full bg-[var(--slate)] rounded-full transition-all"
                style={{ width: `${playheadProgress * 100}%` }}
              />
            </div>
            <span className="text-[10px] text-[var(--text-ter)] font-mono w-7 text-right">
              {Math.round(playheadProgress * 100)}%
            </span>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Scrollable main area (sticky columns/header inside) ── */}
        <div className="flex-1 overflow-auto">
          {filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-24 px-8">
              <Activity className="h-10 w-10 text-[var(--text-ter)] mb-3" />
              <p className="text-[var(--text-ter)] font-medium">No items match your filters</p>
              <p className="text-[var(--text-ter)] text-sm mt-1">
                Try clearing the search or changing the status filter.
              </p>
            </div>
          ) : (
            // Inner wrapper — wide enough for label column + canvas
            <div style={{ width: LABEL_W + canvasWidth, minWidth: '100%' }}>

              {/* ── Sticky header row ── */}
              <div
                className="flex sticky top-0 z-20 bg-[var(--bg-raised)] border-b border-[var(--rule)]"
                style={{ height: RULER_HEIGHT }}
              >
                {/* Sticky-left corner cell */}
                <div
                  className="flex-shrink-0 sticky left-0 z-30 bg-[var(--bg-raised)] border-r border-[var(--rule)] flex items-end pb-2 px-3"
                  style={{ width: LABEL_W }}
                >
                  <span className="text-[10px] text-[var(--text-ter)] font-medium uppercase tracking-wider">
                    Item
                  </span>
                </div>
                {/* Ruler */}
                <div className="flex-shrink-0" style={{ width: canvasWidth }}>
                  <TimeRuler tMin={tMin} tMax={tMax} canvasWidth={canvasWidth} />
                </div>
              </div>

              {/* ── Item rows ── */}
              {itemData.map(({ item, events }, rowIdx) => (
                <div
                  key={item.id}
                  className="flex border-b border-[var(--rule)]"
                  style={{ height: ROW_H + 20 }} // +20 for date labels below dots
                >
                  {/* Sticky-left label cell */}
                  <div
                    className={`flex-shrink-0 sticky left-0 z-10 border-r border-[var(--rule)] flex items-center px-3 gap-1.5 ${
                      rowIdx % 2 === 0 ? 'bg-[var(--surface)]/60' : 'bg-[var(--bg-raised)]'
                    }`}
                    style={{ width: LABEL_W }}
                  >
                    <span
                      className="text-xs font-medium text-[var(--text-pri)] truncate flex-1 leading-tight"
                      title={item.title}
                    >
                      {item.title ?? <span className="text-[var(--text-ter)] italic">Untitled</span>}
                    </span>
                    <Link
                      to={`/prioritization/items/${item.id}/journey`}
                      className="flex-shrink-0 text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
                      title="View full item journey"
                      tabIndex={-1}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>

                  {/* Event canvas cell */}
                  <div
                    className={`flex-shrink-0 ${rowIdx % 2 === 0 ? 'bg-[var(--surface)]/30' : 'bg-[var(--bg-raised)]'}`}
                    style={{ width: canvasWidth, height: ROW_H + 20 }}
                  >
                    <ItemRowSvg
                      events={events}
                      canvasWidth={canvasWidth}
                      playheadProgress={playheadProgress}
                      tMin={tMin}
                      tMax={tMax}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      showPlayhead
                    />
                  </div>
                </div>
              ))}

              {/* Capped notice */}
              {isCapped && (
                <div className="px-4 py-3 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border-t border-[var(--mustard)]">
                  Showing first {ITEM_CAP} items. Refine your search to see others.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right detail panel ── */}
        {selectedEvent && (
          <div
            className="flex-shrink-0 border-l border-[var(--rule)] overflow-hidden shadow-xl"
            style={{ width: 270 }}
          >
            {/* Item link at top of panel */}
            {(() => {
              const ownerItem = itemData.find(({ events }) =>
                events.some((e) => e.id === selectedId),
              )?.item
              return ownerItem ? (
                <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--rule)] bg-[var(--surface)]">
                  <span className="text-xs text-[var(--text-ter)] truncate flex-1" title={ownerItem.title}>
                    {ownerItem.title}
                  </span>
                  <Link
                    to={`/prioritization/items/${ownerItem.id}/journey`}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Journey
                  </Link>
                </div>
              ) : null
            })()}
            <DetailPanel
              event={selectedEvent}
              actorDisplayMap={actorDisplayMap}
              onClose={() => setSelectedId(null)}
            />
          </div>
        )}
      </div>
    </div>
  )
}
